// src/dashboard/pages/teacher/Objectifs/hooks/useObjectifsDashboard.js

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { getGroupsList } from "../../../../../services/groupServices";
import { getSettings } from "../../../../../services/settingsServices";
import { getAllAdherentsListWithGroup } from "../../../../../services/adherentServices";

import {
  getAllObjectifs,
  createObjectif,
  updateObjectif,
  deleteObjectif,
  setObjectifDone,
  setObjectifProgress,
} from "../../../../../services/objectifsServices";

import {
  buildObjectifPayload,
  calculateObjectifsStats,
  filterAdherentsByGroup,
  filterObjectifs,
  getSeasonOptionsFromActive,
  sortAdherents,
  sortObjectifs,
} from "../utils/objectifsDashboardUtils";

const defaultFilters = {
  query: "",
  group: "",
  adherentId: "",
  season: "",
  type: "",
  status: "",
};

export function useObjectifsDashboard() {
  const [objectifs, setObjectifs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [adherents, setAdherents] = useState([]);
  const [seasonOptions, setSeasonOptions] = useState([]);

  const [filters, setFilters] = useState(defaultFilters);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingProgressId, setUpdatingProgressId] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [currentObjectif, setCurrentObjectif] = useState(null);

  const progressTimersRef = useRef({});

  const initDashboard = useCallback(async () => {
    try {
      setLoading(true);

      const [settingsResponse, adherentsResponse, groupsResponse] =
        await Promise.all([
          getSettings(),
          getAllAdherentsListWithGroup(),
          getGroupsList(),
        ]);

      const settings = Array.isArray(settingsResponse)
        ? settingsResponse[0]
        : settingsResponse;

      const activeSeason = settings?.saison_active || "";
      const seasons = getSeasonOptionsFromActive(activeSeason);

      setSeasonOptions(seasons);
      setGroups(Array.isArray(groupsResponse) ? groupsResponse : []);
      setAdherents(
        (Array.isArray(adherentsResponse) ? adherentsResponse : []).sort(
          sortAdherents
        )
      );

      setFilters((prev) => ({
        ...prev,
        season: prev.season || activeSeason || seasons[1] || seasons[0] || "",
      }));
    } catch (error) {
      console.error("Erreur initialisation objectifs :", error);
      toast.error("Erreur lors du chargement des données.");
      setGroups([]);
      setAdherents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchObjectifs = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        saison: filters.season || undefined,
        type: filters.type || undefined,
        is_done:
          filters.status === "done"
            ? true
            : filters.status === "todo"
            ? false
            : undefined,
      };

      const response = await getAllObjectifs(params);
      const rows = Array.isArray(response) ? response : [];

      setObjectifs(sortObjectifs(rows));
    } catch (error) {
      console.error("Erreur chargement objectifs :", error);
      toast.error("Erreur lors du chargement des objectifs.");
      setObjectifs([]);
    } finally {
      setLoading(false);
    }
  }, [filters.season, filters.type, filters.status]);

  useEffect(() => {
    initDashboard();
  }, [initDashboard]);

  useEffect(() => {
    if (!filters.season) return;
    fetchObjectifs();
  }, [filters.season, filters.type, filters.status, fetchObjectifs]);

  const filteredAdherents = useMemo(() => {
    return filterAdherentsByGroup(adherents, filters.group);
  }, [adherents, filters.group]);

  const filteredObjectifs = useMemo(() => {
    return filterObjectifs(objectifs, filters);
  }, [objectifs, filters]);

  const stats = useMemo(() => {
    return calculateObjectifsStats(objectifs, filteredObjectifs);
  }, [objectifs, filteredObjectifs]);

  const openCreateDrawer = () => {
    setCurrentObjectif(null);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const openEditDrawer = (objectif) => {
    setCurrentObjectif(objectif);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
    setCurrentObjectif(null);
  };

  const handleCreateObjectif = async (formData) => {
    const payload = buildObjectifPayload(formData);

    if (!payload.adherent_id) {
      toast.warn("Sélectionnez un adhérent.");
      return;
    }

    if (!payload.title) {
      toast.warn("Le titre est requis.");
      return;
    }

    try {
      setSaving(true);

      await createObjectif(payload);

      toast.success("Objectif créé.");
      setDrawerOpen(false);

      await fetchObjectifs();
    } catch (error) {
      console.error("Erreur création objectif :", error);
      toast.error("Erreur lors de la création de l’objectif.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateObjectif = async (id, formData) => {
    if (!id) {
      toast.error("Objectif introuvable.");
      return;
    }

    const payload = buildObjectifPayload(formData);

    if (!payload.title) {
      toast.warn("Le titre est requis.");
      return;
    }

    try {
      setSaving(true);

      await updateObjectif(id, payload);

      toast.success("Objectif mis à jour.");
      setDrawerOpen(false);
      setCurrentObjectif(null);

      await fetchObjectifs();
    } catch (error) {
      console.error("Erreur mise à jour objectif :", error);
      toast.error("Erreur lors de la mise à jour de l’objectif.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteObjectif = async (row) => {
    if (!row?.id) {
      toast.error("Objectif introuvable.");
      return;
    }

    if (!window.confirm(`Supprimer l’objectif « ${row.title} » ?`)) {
      return;
    }

    try {
      setDeletingId(row.id);

      await deleteObjectif(row.id);

      toast.success("Objectif supprimé.");

      setObjectifs((prev) => prev.filter((item) => item.id !== row.id));

      if (currentObjectif?.id === row.id) {
        setCurrentObjectif(null);
        setDrawerOpen(false);
      }
    } catch (error) {
      console.error("Erreur suppression objectif :", error);
      toast.error("Impossible de supprimer cet objectif.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleDone = async (row) => {
    if (!row?.id) return;

    const nextValue = !row.is_done;
    const previous = objectifs;

    setObjectifs((prev) =>
      prev.map((item) =>
        item.id === row.id
          ? {
              ...item,
              is_done: nextValue,
              progress: nextValue ? 100 : item.progress,
            }
          : item
      )
    );

    try {
      await setObjectifDone(row.id, nextValue);
      toast.success(nextValue ? "Objectif terminé." : "Objectif rouvert.");
    } catch (error) {
      console.error("Erreur statut objectif :", error);
      setObjectifs(previous);
      toast.error("Impossible de changer l’état.");
    }
  };

  const handleChangeProgress = (row, value) => {
    if (!row?.id) return;

    const progress = Math.max(0, Math.min(100, Number(value) || 0));

    setObjectifs((prev) =>
      prev.map((item) =>
        item.id === row.id
          ? {
              ...item,
              progress,
              is_done: progress >= 100 ? true : item.is_done,
            }
          : item
      )
    );

    clearTimeout(progressTimersRef.current[row.id]);

    progressTimersRef.current[row.id] = setTimeout(async () => {
      try {
        setUpdatingProgressId(row.id);
        await setObjectifProgress(row.id, progress);
      } catch (error) {
        console.error("Erreur progression objectif :", error);
        toast.error("Échec de la mise à jour de la progression.");
        await fetchObjectifs();
      } finally {
        setUpdatingProgressId(null);
      }
    }, 400);
  };

  const refreshObjectifs = useCallback(async () => {
    await fetchObjectifs();
  }, [fetchObjectifs]);

  return {
    objectifs,
    filteredObjectifs,
    groups,
    adherents,
    filteredAdherents,
    seasonOptions,
    stats,

    filters,
    setFilters,

    loading,
    saving,
    deletingId,
    updatingProgressId,

    drawerOpen,
    drawerMode,
    currentObjectif,

    openCreateDrawer,
    openEditDrawer,
    closeDrawer,

    handleCreateObjectif,
    handleUpdateObjectif,
    handleDeleteObjectif,
    handleToggleDone,
    handleChangeProgress,
    refreshObjectifs,
  };
}